
  /**
   * @license
   * author: epignosis front-end team
   * @epignosis_llc/gnosis.js v6.0.0
   * Released under the MIT license.
   */

import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2 } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import '../../../../node_modules/core-js/modules/es.array.concat.js';
import '../../../../node_modules/core-js/modules/es.array.reduce.js';
import '../../../../node_modules/core-js/modules/es.object.to-string.js';
import { forwardRef, useRef, isValidElement } from 'react';
import classNames from '../../../../node_modules/classnames/index.js';
import SvgInfoCircled from '../../../icons/legacy/info-circled.svg.js';
import SvgAddOperator from '../../../icons/legacy/add-operator.svg.js';
import Label from '../Label/Label.js';
import Tooltip from '../../Tooltip/Tooltip.js';
import { resolveStyles, selectContainer } from './styles.js';
import { MAX_MENU_HEIGHT, PLACEHOLDER, MIN_WIDTH, MAX_WIDTH } from './constants.js';
import { containerClassNames, renderSelect } from './helpers.js';
import CustomMultiValueLabel from './components/CustomMultiValueLabel.js';
import CustomSingleValue from './components/CustomSingleValue.js';
import CustomOptionComponent from './components/CustomOptionComponent.js';
import { createElement as jsx } from '../../../../node_modules/@emotion/react/dist/emotion-react.browser.esm.js';

var _excluded = ["id", "type", "required", "label", "options", "size", "inline", "status", "isInlineFlex", "isMulti", "creatableTooltip", "maxMenuHeight", "minWidth", "maxWidth", "placeholder", "tooltipContent", "minNumberOfOptionsToEnableSearch", "isValidNewOption", "menuMinWidth", "loadOptions", "isSearchable", "allowTextWrap", "onChange"];
var Select = function Select(props, forwardedRef) {
  var _props$id = props.id,
    id = _props$id === void 0 ? "" : _props$id,
    _props$type = props.type,
    type = _props$type === void 0 ? "select" : _props$type,
    _props$required = props.required,
    required = _props$required === void 0 ? false : _props$required,
    _props$label = props.label,
    label = _props$label === void 0 ? "" : _props$label,
    _props$options = props.options,
    options = _props$options === void 0 ? [] : _props$options,
    _props$size = props.size,
    size = _props$size === void 0 ? "md" : _props$size,
    _props$inline = props.inline,
    inline = _props$inline === void 0 ? false : _props$inline,
    _props$status = props.status,
    status = _props$status === void 0 ? "valid" : _props$status,
    _props$isInlineFlex = props.isInlineFlex,
    isInlineFlex = _props$isInlineFlex === void 0 ? false : _props$isInlineFlex,
    _props$isMulti = props.isMulti,
    isMulti = _props$isMulti === void 0 ? false : _props$isMulti,
    _props$creatableToolt = props.creatableTooltip,
    creatableTooltip = _props$creatableToolt === void 0 ? "Create" : _props$creatableToolt,
    _props$maxMenuHeight = props.maxMenuHeight,
    maxMenuHeight = _props$maxMenuHeight === void 0 ? MAX_MENU_HEIGHT : _props$maxMenuHeight,
    _props$minWidth = props.minWidth,
    minWidth = _props$minWidth === void 0 ? MIN_WIDTH : _props$minWidth,
    _props$maxWidth = props.maxWidth,
    maxWidth = _props$maxWidth === void 0 ? MAX_WIDTH : _props$maxWidth,
    _props$placeholder = props.placeholder,
    placeholder = _props$placeholder === void 0 ? PLACEHOLDER : _props$placeholder,
    _props$tooltipContent = props.tooltipContent,
    tooltipContent = _props$tooltipContent === void 0 ? "" : _props$tooltipContent,
    _props$minNumberOfOpt = props.minNumberOfOptionsToEnableSearch,
    minNumberOfOptionsToEnableSearch = _props$minNumberOfOpt === void 0 ? 10 : _props$minNumberOfOpt,
    isValidNewOption = props.isValidNewOption,
    menuMinWidth = props.menuMinWidth,
    loadOptions = props.loadOptions,
    isSearchable = props.isSearchable,
    allowTextWrap = props.allowTextWrap,
    _onChange = props.onChange,
    rest = _objectWithoutProperties(props, _excluded);
  var hasLabel = Boolean(label);
  var containerRef = useRef(null);
  var labelClassname = classNames({
    required: required
  });
  var countOptions = function countOptions() {
    // Count the number of options, including nested options if exists
    return options.reduce(function (count, option) {
      return count + ("options" in option ? option.options.length : 1);
    }, 0);
  };
  var isSelectSearchable = function isSelectSearchable() {
    if (isSearchable !== undefined) {
      return isSearchable;
    }
    var isAsyncType = type === "async";
    var hasManyOptions = countOptions() > minNumberOfOptionsToEnableSearch;
    return isAsyncType || hasManyOptions;
  };
  var shouldRenderTooltip = tooltipContent && typeof tooltipContent === "string" && tooltipContent !== "" || /*#__PURE__*/isValidElement(tooltipContent);
  var styles = resolveStyles({
    size: size,
    menuMinWidth: menuMinWidth,
    allowTextWrap: allowTextWrap
  });
  var formatCreateLabel = function formatCreateLabel(inputValue) {
    return jsx(Tooltip, {
      content: creatableTooltip,
      parentProps: {
        "aria-label": inputValue
      }
    }, jsx("div", {
      className: "select-create-label"
    }, jsx("span", null, inputValue, " "), jsx("span", null, jsx(SvgAddOperator, {
      color: "black",
      height: 12
    }))));
  };
  var customSelectProps = _objectSpread2(_objectSpread2({}, rest), {}, {
    id: id,
    "aria-label": rest["aria-label"],
    ref: forwardedRef,
    styles: styles,
    isMulti: isMulti,
    classNames: {
      control: function control() {
        return containerClassNames(status, size);
      },
      option: function option(_ref) {
        var isSelected = _ref.isSelected;
        return "".concat(isSelected ? "selected" : "", " option-").concat(size);
      }
    },
    components: {
      IndicatorSeparator: function IndicatorSeparator() {
        return null;
      },
      Option: CustomOptionComponent,
      SingleValue: CustomSingleValue,
      MultiValueLabel: CustomMultiValueLabel
    },
    formatCreateLabel: formatCreateLabel,
    isSearchable: isSelectSearchable(),
    maxMenuHeight: maxMenuHeight,
    options: options,
    placeholder: placeholder,
    type: type,
    isValidNewOption: isValidNewOption,
    loadOptions: loadOptions,
    onMouseDown: function onMouseDown(e) {
      return e.stopPropagation();
    },
    onChange: function onChange(option, action) {
      _onChange === null || _onChange === void 0 || _onChange(option, action);
    },
    isOptionDisabled: function isOptionDisabled(option) {
      return Boolean(option.disabled);
    }
  });
  return jsx("div", {
    css: function css(theme) {
      return selectContainer(theme, {
        size: size,
        inline: inline,
        isInlineFlex: isInlineFlex,
        minWidth: minWidth,
        maxWidth: maxWidth
      });
    }
  }, hasLabel && jsx("div", {
    className: "label-container"
  }, jsx(Label, {
    htmlFor: id,
    "aria-labelledby": id,
    "data-testid": "".concat(id, "-label"),
    className: labelClassname
  }, label), shouldRenderTooltip && jsx("div", {
    "data-testid": "".concat(id, "-tooltip")
  }, jsx(Tooltip, {
    content: tooltipContent,
    parentProps: {
      "aria-label": label
    }
  }, jsx(SvgInfoCircled, {
    height: 20
  })))), jsx("div", {
    className: "select-input-wrapper",
    "data-testid": "".concat(id, "-select"),
    ref: containerRef
  }, renderSelect(type, customSelectProps)));
};
var Select$1 = /*#__PURE__*/forwardRef(Select);

export { Select$1 as default };
//# sourceMappingURL=Select.js.map
